import { createContext, type ReactNode, useContext } from 'react';
import { createPortal } from 'react-dom';

type FooterHost = HTMLElement | null;
const CanvasFooterContext = createContext<FooterHost>(null);
const CanvasHeaderContext = createContext<FooterHost>(null);
const CanvasHeaderLeadContext = createContext<FooterHost>(null);

export function CanvasFooterHost({ host, children }: { host: FooterHost; children: ReactNode }) {
  return <CanvasFooterContext.Provider value={host}>{children}</CanvasFooterContext.Provider>;
}

export function CanvasHeaderHost({
  host,
  leadHost = null,
  children,
}: {
  host: FooterHost;
  /** The left side of the header, beside the kind label (e.g. Preview · Select). */
  leadHost?: FooterHost;
  children: ReactNode;
}) {
  return (
    <CanvasHeaderContext.Provider value={host}>
      <CanvasHeaderLeadContext.Provider value={leadHost}>
        {children}
      </CanvasHeaderLeadContext.Provider>
    </CanvasHeaderContext.Provider>
  );
}

/** A control that sits on the left of the canvas header, next to the kind label. */
export function CanvasHeaderLeadTools({ children }: { children: ReactNode }) {
  const host = useContext(CanvasHeaderLeadContext);
  if (!host) return null;
  return createPortal(<span className="ws-canvas-lead-tools">{children}</span>, host);
}

/** Keeps the action area outside the canvas scroller while its owner stays with the tab. */
export function CanvasFooter({
  children,
  className = '',
  hidden = false,
}: {
  children: ReactNode;
  className?: string;
  hidden?: boolean;
}) {
  const host = useContext(CanvasFooterContext);
  if (!host) return null;
  return createPortal(
    <footer className={`ws-canvas-footer ${className}`.trim()} data-canvas-footer hidden={hidden}>
      {children}
    </footer>,
    host,
  );
}

export function CanvasHeaderTools({ children }: { children: ReactNode }) {
  const host = useContext(CanvasHeaderContext);
  if (!host) return null;
  return createPortal(<span className="ws-canvas-header-tools">{children}</span>, host);
}
